import { Component, ElementRef, Inject, Input, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StyleManagerService } from '@wms/app/services/style-manager/style-manager.service';
import {FileService, ProjectService} from '@wms/app/services';
import { environment } from 'src/environments/environment';
import { APP_OVERRIDE_CSS_PATH, getProjectResourcePath, convertTokenToVariable } from '@wms/core';
import { isEqual } from 'lodash';
import { FoundationService } from '@wms/app/workspace/style-workspace/foundation/services/foundationService/foundation.service';
import { Subscription } from 'rxjs';

/**
 * This wrapper component encapsulates the canvas with all the relevant styles like foundation.css, app.css
 * etc. and isolates it from any global styles.
 */
@Component({
  selector: 'wms-canvas-sandbox-wrapper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wms-canvas-sandbox-wrapper.component.html',
  styleUrls: ['./wms-canvas-sandbox-wrapper.component.less'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class WmsCanvasSandboxWrapper {
  @Input() attributes?: any;
  public projectService: ProjectService;
  public fileService: FileService;
  private subscriptions: Subscription[] = [];

  @ViewChild('shadowHost', { static: true }) shadowHost!: ElementRef;

  constructor(private styleManager: StyleManagerService, @Inject("APP_BUILD_META_INFO") private appBaseMetaInfo: any, private elementRef: ElementRef, private renderer: Renderer2, private foundationService: FoundationService) {
    this.projectService = inject(ProjectService);
    this.fileService = inject(FileService);
  }

  ngAfterViewInit() {
    const cssUrls = [];
    const basePath = this.appBaseMetaInfo.CDN_BASE_URL ? this.appBaseMetaInfo.CDN_BASE_URL + 'assets' : 'assets';
    const themePath = getProjectResourcePath(this.projectService.getProjectId()) + `${APP_OVERRIDE_CSS_PATH}`;
    if(this.projectService.isPrism() && this.projectService.isApplicationType()) {
      cssUrls.push(basePath + '/styles/foundation-css/foundation.css');
      cssUrls.push(themePath); // Added override.css to oveeride foundation.css
      cssUrls.push(basePath + '/nvd3/nv.d3.min.css'); // nvd3
      cssUrls.push(basePath + '/calendar/main.css'); // calendar

      // Only need app.css here for appplication type. Not needed for prefabs
      if (this.projectService.isApplicationType()) {
        let appCssPath = environment.SERVICES_BASE_PATH + 'services/projects/' + this.projectService.getProjectId()
          + '/resources/content/'
          + (this.projectService.isNativeMobileApplicationType() ? 'project/generated-rn-app/designtime/' : 'web/')
          + 'app.css';
        cssUrls.push(appCssPath);

      }
      this.loadCustomIcons(this.projectService, this.fileService);
    }
    else{
      cssUrls.push(basePath + '/styles/runtime-css/wm-style.css');
      this.loadCustomIcons(this.projectService, this.fileService);
      let theme = this.projectService.getActiveTheme();
      const themePath = getProjectResourcePath(this.projectService.getProjectId()) + 'themes/' + theme + '/style.css';

      if (this.projectService.isWebApplicationType()) {
        cssUrls.push(getProjectResourcePath(this.projectService.getProjectId()) + 'app.css');
      }
      cssUrls.push(themePath)
    }
    this.addStylesToShadowDOM(cssUrls);
  }

    loadCustomIcons(projService: ProjectService, fileService: FileService){
        fileService.read('font.config.js').then((result: any) => {
            const projectID = projService.getProjectId();
            const cssPaths: string[] = [];
            const regex = /"csspath":\s*"([^"]+)"/g;
            let match;
            while ((match = regex.exec(result)) !== null) {
                cssPaths.push('/studio/services/projects/' + projectID + '/resources/info/web/' + match[1]);
            }
            this.addStylesToShadowDOM(cssPaths);
        });
    }

  ngOnChanges(changes: SimpleChanges) {
    if (!isEqual(changes['attributes'].previousValue, changes['attributes'].currentValue)) {
      let host = this.elementRef.nativeElement;
      this.attributes.forEach((attribute: any) => {
        Object.entries(attribute).forEach(([key, value]) => {
          $(host).attr(key, (value as string));
        });
      });
    }
  }

  /* Adds styles dynamically to the shadow dom */
  addStylesToShadowDOM(styleUrls: Array<string>) {
    const shadowRoot = (this.elementRef.nativeElement as HTMLElement).shadowRoot as ShadowRoot;

    const fetchPromises = styleUrls.map(styleUrl => {
      return this.fetchStyleSheetandCreateBlobURL(styleUrl).then((blobURL) => {
        // Create a <link> element
        const linkElement = this.renderer.createElement('link');
        this.renderer.setAttribute(linkElement, 'rel', 'stylesheet');
        this.renderer.setAttribute(linkElement, 'href', blobURL);
        styleUrl.includes(APP_OVERRIDE_CSS_PATH) && this.renderer.setAttribute(linkElement, 'id', 'overridesLink');

        // Append the <link> element to the Shadow DOM
        shadowRoot.appendChild(linkElement);
      })
    });
    Promise.all(fetchPromises)
      .then(() => {

      })
      .catch(error => {
        console.error('Error waiting for all stylesheets to load:', error);
      }).finally(() => {
        let subscription1 = this.foundationService.updatedTokensCache$.subscribe(() => {
          this.setCssVariablesToShadowDOM(this.foundationService.updatedTokensCache);
        });
        this.subscriptions.push(subscription1);
        let subscription2 = this.foundationService.reloadAppOverrides$.subscribe((save: boolean) => {
          if (save) {
            const overridesLink = shadowRoot.querySelector('link#overridesLink');
            let overridesUrl: any = styleUrls.find(styleUrl => styleUrl.includes(APP_OVERRIDE_CSS_PATH));
            if (overridesUrl) {
                this.fetchStyleSheetandCreateBlobURL(overridesUrl).then((blobURL) => {
                  // Update the href attribute of the overrides <link> tag
                  if (overridesLink) this.renderer.setProperty(overridesLink, 'href', blobURL);
                  else {
                    const shadowRoot = (this.elementRef.nativeElement as HTMLElement).shadowRoot as ShadowRoot;
                    const linkElement = this.renderer.createElement('link');
                    this.renderer.setAttribute(linkElement, 'rel', 'stylesheet');
                    this.renderer.setAttribute(linkElement, 'href', blobURL);
                    this.renderer.setAttribute(linkElement, 'id', 'overridesLink');
                    shadowRoot.appendChild(linkElement);
                  }
                })
            }
          }
        });
        this.subscriptions.push(subscription2);
      })
  }

  fetchStyleSheetandCreateBlobURL(styleUrl: string) {
    return fetch(styleUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load stylesheet: ${response.statusText}`);
        }
        return response.text();
      })
      .then(styleContent => {
        // Extract @font-face declarations
        const fontFaceDeclarations = styleContent.match(/@font-face\s*{[^}]*}/g) || [];

        // Create and append font-face styles to document head
        if (fontFaceDeclarations.length > 0) {
          const fontStyles = fontFaceDeclarations.join('\n').replace(
            /url\(["']?(.+?)["']?\)/g,
            (match, url) => {
              if (/^(http|https|data):/.test(url)) {
                return match;
              }
              const absoluteUrl = new URL(url, new URL(styleUrl, window.location.origin)).toString();
              return `url("${absoluteUrl}")`;
            }
          );
          const fontStyleElement = document.createElement('style');
          fontStyleElement.textContent = fontStyles;
          document.head.appendChild(fontStyleElement);
        }

        // Remove font-face declarations from the main styles
        let updatedStyleContent = styleContent.replace(/@font-face\s*{[^}]*}/g, '');

        const regexForRootSelectors = /:root\[(.*?)\]/;
        // Replace :root with :host
        updatedStyleContent = styleContent.replace(regexForRootSelectors, ":root([$1])").replace(/:root/g, ':host');

        // Fix relative URLs for other resources
        const baseUrl = new URL(styleUrl, window.location.origin).toString();
        updatedStyleContent = updatedStyleContent.replace(
          /url\(["']?(.+?)["']?\)/g,
          (match, url) => {
            if (/^(http|https|data):/.test(url)) {
              return match;
            }
            const absoluteUrl = new URL(url, baseUrl).toString();
            return `url("${absoluteUrl}")`;
          }
        );

        // Create a Blob with the CSS content
        const blob = new Blob([updatedStyleContent], { type: 'text/css' });
        const blobURL = URL.createObjectURL(blob);
        return Promise.resolve(blobURL);
      })
      .catch(error => {
        console.error('Error loading or processing stylesheet:', error);
        return Promise.reject(error);
      })
  }

  setCssVariablesToShadowDOM(tokens?: any): void {
    const shadowRoot = (this.elementRef.nativeElement as HTMLElement).shadowRoot as ShadowRoot;
    const existingStyles = shadowRoot.querySelectorAll('style#tokenCacheStyles') ?? [];
    existingStyles.forEach((style) => style.remove());

    if (tokens && Object.keys(tokens).length > 0) {
      const style = this.renderer.createElement('style');
      const selectorMap: Record<string, string[]> = {};
      Object.keys(tokens).forEach(category => {
        Object.keys(tokens[category]).forEach(key => {
         const selectorEntry = this.foundationService.updatedTokenSelectors?.[category]?.[key];
          const selector = (selectorEntry?.selector === ':root' || !selectorEntry?.selector)
            ? ':host'
            : selectorEntry?.selector
              .split(',')
              .map((s: string) => s.trim())
              .map((s: string) => s === ':root' ? ':host' : `.wm-app ${s}`)
              .join(', ');
        const name = selectorEntry?.name;

          const { cssVarName, cssVarValue } = convertTokenToVariable(tokens, category, key, name);
          selectorMap[selector] ??= [];
          selectorMap[selector].push(`  ${cssVarName}: ${cssVarValue};`);
        });
      });

      let styleContent = '';
      for (const selector in selectorMap) {
        styleContent += `${selector} {\n${selectorMap[selector].join('\n')}\n}\n`;
      }
      style.setAttribute('id', 'tokenCacheStyles')
      style.textContent = styleContent;
      style.textContent = style.textContent + style.textContent.replace(':host', `:host([color='dark'])`) ;
      shadowRoot.appendChild(style);
      this.renderer.insertBefore(shadowRoot, style, shadowRoot.lastChild?.nextSibling);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
